import React, { useState, useEffect } from "react";

import { arrayOf, bool, oneOfType, string } from "prop-types";
import { AnimatePresence, motion } from "framer-motion";
import { useMedia } from "react-use";
import { useTranslation } from "@lib/i18n";
import { useDispatch } from "react-redux";
import { useRouter } from "next/router";

import FilterResultItem from "@components/TestDrive/FilterResultItem";

import gtm from "@gtm/core";
import Typography from "@components/shared/Typography";
import ArrowRightIcon from "@components/shared/Icons/ArrowRight";
import ArrowLeftIcon from "@components/shared/Icons/ArrowLeft";
import Icon from "@components/shared/Icons/Icon";
import SimpleButton from "@components/shared/SimpleButton";
import { toast } from "@lib/reactToastify";

import { testDriveLocationType, testDriveVehicleType } from "@types";
import { useIsUserTrustedSelector } from "@redux/reducers/auth";
import { ROUTES } from "@shared/routing/routes";
import { testDriveMoreResultsEvent } from "@gtm/events/testdrive";
import { useRoutingUtils } from "@hooks/useRoutingUtils";

import {
  TEST_DRIVE_TYPE_ID,
  CTA_TD_TYPES,
  DEAL_TYPE,
  LP_DEMOOZ_URL,
} from "@shared/constants";

import {
  setTestDrivePointAction,
  setTestDriveSlotAction,
} from "@redux/actions/testDriveActions";

import {
  createNewDealFromExistingAction,
  postDealAction,
} from "@redux/actions/dealActions";

import {
  useCurrentDealSelector,
  useLastCtaSelector,
} from "@redux/reducers/deal";

const FilterResultList = ({ testDrivePoints = [], isOpen, activePointId }) => {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const routingUtils = useRoutingUtils();
  const dispatch = useDispatch();
  const isUserTrusted = useIsUserTrustedSelector();
  const currentDeal = useCurrentDealSelector();
  const lastCta = useLastCtaSelector();

  const [isOpenRemainingSection, setIsOpenRemainingSection] = useState(false);
  const nearest = testDrivePoints.slice(0, 4);
  const remaining = testDrivePoints.slice(4, 10);
  const isDesktopView = useMedia(`(min-width: 1024px)`);

  const isTestDriveCompleted =
    currentDeal?.dealType === DEAL_TYPE.TEST_DRIVE &&
    lastCta === CTA_TD_TYPES.RESERVATION_COMPLETED;

  const handleItemPick = async point => {
    dispatch(setTestDrivePointAction(point));
    dispatch(setTestDriveSlotAction(null));

    // Create new deal only if existing deal is not TD or finished TD
    // otherwise just update deal
    if (
      isUserTrusted &&
      (currentDeal?.dealType !== DEAL_TYPE.TEST_DRIVE ||
        isTestDriveCompleted ||
        point.type === TEST_DRIVE_TYPE_ID.FNAC_DARTY)
    ) {
      await dispatch(createNewDealFromExistingAction());
    }

    return dispatch(
      postDealAction(CTA_TD_TYPES.LOCATION_SELECTED, DEAL_TYPE.TEST_DRIVE)
    );
  };

  const getTargetRouteObject = point => {
    switch (point.type) {
      case TEST_DRIVE_TYPE_ID.STORE:
        return {
          route: ROUTES.TEST_DRIVE_DETAIL_ENSTORE,
          asPath: routingUtils.getLocalizedPathname(
            ROUTES.TEST_DRIVE_DETAIL_ENSTORE,
            { testDrivePointSlug: point.slug },
            i18n.language
          ),
        };

      case TEST_DRIVE_TYPE_ID.FNAC_DARTY:
        return {
          route: ROUTES.TEST_DRIVE_DETAIL_FNAC_DARTY,
          asPath: routingUtils.getLocalizedPathname(
            ROUTES.TEST_DRIVE_DETAIL_FNAC_DARTY,
            { testDrivePointSlug: point.slug },
            i18n.language
          ),
        };

      case TEST_DRIVE_TYPE_ID.HOME:
        return {
          route: ROUTES.TEST_DRIVE_HOME,
          asPath: routingUtils.getLocalizedPathname(
            ROUTES.TEST_DRIVE_HOME,
            null,
            i18n.language
          ),
        };

      case TEST_DRIVE_TYPE_ID.MEDIAMARKT:
        return {
          route: ROUTES.TEST_DRIVE_DETAIL_MEDIAMARKT,
          asPath: routingUtils.getLocalizedPathname(
            ROUTES.TEST_DRIVE_DETAIL_MEDIAMARKT,
            { testDrivePointSlug: point.slug },
            i18n.language
          ),
        };

      case TEST_DRIVE_TYPE_ID.SUPER:
        return {
          route: LP_DEMOOZ_URL + t("testDrive.demoozAmbassadorPath"),
          asPath: LP_DEMOOZ_URL + t("testDrive.demoozAmbassadorPath"),
        };

      case TEST_DRIVE_TYPE_ID.LAMAISON:
      default:
        return {
          route: ROUTES.TEST_DRIVE_DETAIL_LA_MAISON,
          asPath: routingUtils.getLocalizedPathname(
            ROUTES.TEST_DRIVE_DETAIL_LA_MAISON,
            { testDrivePointSlug: point.slug },
            i18n.language
          ),
        };
    }
  };

  const onRequestSuccess = async point => {
    const { route, asPath } = getTargetRouteObject(point);

    await router.push(route, asPath);

    return new Promise(() => null);
  };

  const toggleOpen = () => {
    setIsOpenRemainingSection(!isOpenRemainingSection);
    gtm.fire(testDriveMoreResultsEvent());
  };

  useEffect(() => {
    if (!isOpenRemainingSection && isDesktopView) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isOpenRemainingSection]);

  const onFilterResultItemError = error => {
    toast.error(t("errors.default"), {}, error);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div>
      <Typography className="text-lg uppercase font-semibold mb-4">
        {t("testDrive.filterResults")}
      </Typography>
      <div className="flex flex-col gap-4">
        {nearest.map(point => (
          <FilterResultItem
            activePointId={activePointId}
            key={point.id}
            onRequestError={onFilterResultItemError}
            onRequestSuccess={() => onRequestSuccess(point)}
            point={point}
            triggerRequestCallback={() => handleItemPick(point)}
          />
        ))}
        <AnimatePresence>
          {isOpenRemainingSection && (
            <motion.div
              animate={{
                height: "auto",
                display: "flex",
              }}
              className="flex flex-col gap-4 overflow-hidden"
              exit={{
                height: 0,
                transitionEnd: { display: "none" },
              }}
              initial={{
                height: 0,
                display: "none",
              }}
              transition={{ duration: 0.6 }}
            >
              {remaining.map(point => (
                <FilterResultItem
                  activePointId={activePointId}
                  key={point.id}
                  onRequestError={onFilterResultItemError}
                  onRequestSuccess={() => onRequestSuccess(point)}
                  point={point}
                  triggerRequestCallback={() => handleItemPick(point)}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {remaining.length > 0 && (
        <SimpleButton
          className="m-0 mt-4 px-0 py-2"
          endAdornment={
            !isOpenRemainingSection && (
              <Icon className="size-4" icon={<ArrowRightIcon />} />
            )
          }
          onClick={toggleOpen}
          startAdornment={
            isOpenRemainingSection && (
              <Icon className="size-4" icon={<ArrowLeftIcon />} />
            )
          }
        >
          {isOpenRemainingSection
            ? t("testDrive.showLess")
            : t("testDrive.showRemaining")}
        </SimpleButton>
      )}
    </div>
  );
};

FilterResultList.propTypes = {
  activePointId: string,
  isOpen: bool.isRequired,
  testDrivePoints: arrayOf(
    oneOfType([testDriveLocationType, testDriveVehicleType])
  ),
};

export default FilterResultList;
